import React from 'react'
import ReactDOMServer from 'react-dom/server'

import EyeOffIcon from '../assets/svg/EyeOffIcon'
import EyeOnIcon from '../assets/svg/EyeOnIcon'
import Group from '../assets/svg/Remix/Group'
import LocationPinIcon from '../assets/svg/LocationPinIcon'
import LocationPinLoadingIcon from '../assets/svg/LocationPinLoadingIcon'
import Pushpin from '../assets/svg/Remix/Pushpin'
import WaypointIcon from '../assets/svg/WaypointIcon'

// Cache for SVG data URIs to prevent repeated conversions
// Structure: { cacheKey: { url, markup, size, createdAt, accessCount, lastAccessed } }
const svgCache = {}

// Cache for icon components to prevent repeated imports
const iconCache = {}

// Track blob URLs that need cleanup
const activeBlobUrls = new Set()

// Track non-cached blob URLs for cleanup (when caching is disabled)
// Map of URL -> { createdAt, timeoutId }
const nonCachedBlobUrls = new Map()

// Caching is off by default; only enabled when app config (e.g. SVG_CACHING_ENABLED) sets it via setCachingEnabled(true) / preloadSvgCache(true)
let cachingEnabled = false

// Cleanup delay for non-cached blob URLs (5 minutes)
// This gives the browser time to use the URL before we revoke it
const NON_CACHED_CLEANUP_DELAY = 5 * 60 * 1000

// Maximum cache size to prevent unbounded memory growth
// Based on: ~80 icons × 12 colors × 2 widths = ~1,920 combinations
// With ~3KB per entry = ~6MB max cache size
// Majority of users have <2k waypoints, but cache size allows for more unique combinations
const MAX_CACHE_SIZE = 2000

// Maximum age for cache entries (24 hours) - entries older than this will be evicted
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000

/**
 * Get a fallback/default SVG data URI when errors occur
 * @param {string} color - Icon color
 * @param {number} width - Icon width
 * @returns {string} Fallback blob URL
 */
function getFallbackDataURI(color, width) {
  // Use gohunt-logo as fallback icon
  const fallbackColor = color || 'rgba(243,119,53,1)'

  try {
    // Create the gohunt-logo icon component as fallback
    const iconComponent = React.createElement(WaypointIcon, {
      icon: 'gohunt-logo',
      color: fallbackColor,
      width: width
    })

    // Convert to static markup
    const staticMarkup = ReactDOMServer.renderToStaticMarkup(iconComponent)

    if (!staticMarkup || staticMarkup.trim().length === 0) {
      // If even gohunt-logo fails, return empty data URI
      console.error('Failed to create fallback gohunt-logo SVG')
      return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"></svg>'
    }

    return exportSVG(staticMarkup)
  } catch (e) {
    // If even fallback fails, return empty data URI
    console.error('Failed to create fallback gohunt-logo SVG', e)
    return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"></svg>'
  }
}

/**
 * Generate a cache key from component name and props
 * @param {string} componentName - Name of the component
 * @param {object} props - Component props
 * @returns {string} Cache key
 */
function generateCacheKey(componentName, props) {
  if (!props || Object.keys(props).length === 0) {
    return componentName
  }
  // Sort props keys for consistent cache keys
  const sortedKeys = Object.keys(props).sort()
  const propString = sortedKeys.map(key => {
    const value = props[key]
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return `${key}:null`
    }
    return `${key}:${value}`
  }).join('-')
  return `${componentName}-${propString}`
}

// Must match Form/Eyecon and preloadCommonSvgs — pinned so LRU/age eviction never drops them
export const EYE_ON_PRELOAD_PROPS = { color: '#f37735' }
export const EYE_OFF_PRELOAD_PROPS = { color: '#939393' }
const PINNED_SVG_CACHE_KEYS = new Set([
  generateCacheKey('EyeOnIcon', EYE_ON_PRELOAD_PROPS),
  generateCacheKey('EyeOffIcon', EYE_OFF_PRELOAD_PROPS),
  generateCacheKey('LocationPinIcon', {}),
])

function isPinnedCacheKey(key) {
  return PINNED_SVG_CACHE_KEYS.has(key)
}

/**
 * Get cached SVG data URI for any React SVG component
 * @param {React.Component} Component - React component to render
 * @param {string} componentName - Unique name for the component (for cache key)
 * @param {object} props - Props to pass to the component
 * @param {function} fallbackFn - Optional fallback function if rendering fails
 * @returns {string} Blob URL
 */
export function getCachedSvgDataURI(Component, componentName, props = {}, fallbackFn = null) {
  const cacheKey = generateCacheKey(componentName, props)

  // Check cache only if caching is enabled
  if (cachingEnabled && svgCache[cacheKey]) {
    // Update access statistics
    svgCache[cacheKey].accessCount = (svgCache[cacheKey].accessCount || 0) + 1
    svgCache[cacheKey].lastAccessed = Date.now()
    return svgCache[cacheKey].url
  }

  try {
    // Create the component
    const component = React.createElement(Component, props)

    // Convert to static markup
    const staticMarkup = ReactDOMServer.renderToStaticMarkup(component)

    if (!staticMarkup || staticMarkup.trim().length === 0) {
      console.warn('Empty SVG markup generated for component:', componentName)
      if (fallbackFn) {
        return fallbackFn()
      }
      return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"></svg>'
    }

    // Convert to data URI
    const dataURI = exportSVG(staticMarkup)

    // Only cache if caching is enabled
    if (cachingEnabled) {
      // Check if we need to evict old entries before adding new one
      evictOldEntriesIfNeeded()

      // Calculate approximate size in bytes
      const sizeBytes = new Blob([staticMarkup]).size

      // Cache the result with metadata
      svgCache[cacheKey] = {
        accessCount: 1,
        componentName: componentName,
        createdAt: Date.now(),
        lastAccessed: Date.now(),
        markup: staticMarkup,
        props: props,
        size: sizeBytes,
        url: dataURI
      }
    } else {
      // When caching is disabled, schedule cleanup of this blob URL
      // This prevents "too many files open" errors
      scheduleNonCachedCleanup(dataURI)
    }

    return dataURI
  } catch (error) {
    // Log error but don't break the app - return fallback
    console.error('Error generating SVG for component:', componentName, 'error:', error)
    if (fallbackFn) {
      return fallbackFn()
    }
    return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"></svg>'
  }
}

/**
 * Get cached SVG data URI for a waypoint icon
 * @param {string} icon - Icon name
 * @param {string} color - Icon color
 * @param {number} width - Icon width
 * @returns {string} Data URI
 */
export function getCachedWaypointDataURI(icon, color, width) {
  width = width || 34
  const cacheKey = icon + '-' + color + '-' + width

  // Check cache only if caching is enabled
  if (cachingEnabled && svgCache[cacheKey]) {
    // Update access statistics
    svgCache[cacheKey].accessCount = (svgCache[cacheKey].accessCount || 0) + 1
    svgCache[cacheKey].lastAccessed = Date.now()
    return svgCache[cacheKey].url
  }

  try {
    // Create the icon component
    const iconComponent = React.createElement(WaypointIcon, { icon: icon, color: color, width: width })

    // Convert to static markup
    const staticMarkup = ReactDOMServer.renderToStaticMarkup(iconComponent)

    if (!staticMarkup || staticMarkup.trim().length === 0) {
      console.warn('Empty SVG markup generated for icon:', icon, 'color:', color)
      return getFallbackDataURI(color, width)
    }

    // Convert to data URI
    const dataURI = exportSVG(staticMarkup)

    // Only cache if caching is enabled
    if (cachingEnabled) {
      // Check if we need to evict old entries before adding new one
      evictOldEntriesIfNeeded()

      // Calculate approximate size in bytes
      const sizeBytes = new Blob([staticMarkup]).size

      // Cache the result with metadata
      svgCache[cacheKey] = {
        accessCount: 1,
        color: color,
        createdAt: Date.now(),
        icon: icon,
        lastAccessed: Date.now(),
        markup: staticMarkup,
        size: sizeBytes,
        url: dataURI,
        width: width
      }
    } else {
      // When caching is disabled, schedule cleanup of this blob URL
      // This prevents "too many files open" errors
      scheduleNonCachedCleanup(dataURI)
    }

    return dataURI
  } catch (error) {
    // Log error but don't break the app - return fallback icon
    console.error('Error generating SVG for icon:', icon, 'color:', color, 'error:', error)
    return getFallbackDataURI(color, width)
  }
}

/**
 * Evict old or least recently used cache entries if cache is too large
 * Only evicts when necessary and protects recently accessed entries
 */
function evictOldEntriesIfNeeded() {
  const now = Date.now()
  const keysToDelete = []

  // Don't evict entries accessed in the last 15 seconds (likely still in use)
  // Protects entries during bulk rendering operations
  const RECENT_ACCESS_THRESHOLD = 15000

  // First, remove entries older than MAX_CACHE_AGE_MS
  // Use for...in loop (faster than forEach for object iteration)
  for (const key in svgCache) {
    if (isPinnedCacheKey(key)) {
      continue
    }
    const entry = svgCache[key]
    if (entry && entry.createdAt && (now - entry.createdAt) > MAX_CACHE_AGE_MS) {
      keysToDelete.push(key)
    }
  }

  // Delete old entries (but don't revoke URLs - they may still be in use by map graphics)
  for (let i = 0; i < keysToDelete.length; i++) {
    const key = keysToDelete[i]
    const entry = svgCache[key]
    if (entry) {
      // Don't revoke blob URLs during eviction - they may still be referenced by map graphics
      // URLs will be cleaned up when cache is explicitly cleared
      delete svgCache[key]
    }
  }

  // Count remaining entries after age-based eviction
  let currentSize = 0
  for (const key in svgCache) {
    currentSize++
  }

  // Only evict if we're at or over the limit (not just when adding)
  // And only evict enough to make room, not aggressively
  if (currentSize >= MAX_CACHE_SIZE) {
    // Build array of entries with access times for sorting
    const entries = []
    for (const key in svgCache) {
      if (isPinnedCacheKey(key)) {
        continue
      }
      const entry = svgCache[key]
      if (entry) {
        const lastAccessed = entry.lastAccessed || entry.createdAt || 0
        const timeSinceAccess = now - lastAccessed

        // Don't evict entries accessed recently (still in use)
        if (timeSinceAccess > RECENT_ACCESS_THRESHOLD) {
          entries.push({
            key: key,
            lastAccessed: lastAccessed
          })
        }
      }
    }

    // Sort by lastAccessed (oldest first)
    entries.sort(function (a, b) {
      return a.lastAccessed - b.lastAccessed
    })

    // Only remove enough to get below the limit (with small buffer)
    // Remove oldest entries that haven't been accessed recently
    const toRemove = Math.max(1, currentSize - MAX_CACHE_SIZE + 1)
    const actualRemove = Math.min(toRemove, entries.length)

    for (let i = 0; i < actualRemove; i++) {
      const key = entries[i].key
      const entry = svgCache[key]
      if (entry) {
        // Don't revoke blob URLs during eviction - they may still be referenced by map graphics
        // URLs will be cleaned up when cache is explicitly cleared
        delete svgCache[key]
      }
    }
  }
}

/**
 * Schedule cleanup for a non-cached blob URL
 * @param {string} url - Blob URL to clean up
 */
function scheduleNonCachedCleanup(url) {
  // Cancel any existing cleanup for this URL
  if (nonCachedBlobUrls.has(url)) {
    const existing = nonCachedBlobUrls.get(url)
    if (existing.timeoutId) {
      clearTimeout(existing.timeoutId)
    }
  }

  // Schedule cleanup after delay
  const timeoutId = setTimeout(() => {
    try {
      if (activeBlobUrls.has(url)) {
        URL.revokeObjectURL(url)
        activeBlobUrls.delete(url)
      }
      nonCachedBlobUrls.delete(url)
    } catch (e) {
      // URL may already be revoked
      nonCachedBlobUrls.delete(url)
    }
  }, NON_CACHED_CLEANUP_DELAY)

  nonCachedBlobUrls.set(url, {
    createdAt: Date.now(),
    timeoutId: timeoutId
  })
}

/**
 * Clean up all non-cached blob URLs immediately
 */
export function cleanupNonCachedBlobUrls() {
  // Use for...of loop (faster than forEach for Map iteration)
  for (const [url, info] of nonCachedBlobUrls) {
    if (info.timeoutId) {
      clearTimeout(info.timeoutId)
    }
    try {
      if (activeBlobUrls.has(url)) {
        URL.revokeObjectURL(url)
        activeBlobUrls.delete(url)
      }
    } catch (e) {
      // URL may already be revoked
    }
    nonCachedBlobUrls.delete(url)
  }
}

/**
 * Evict old cache entries (public function for periodic cleanup)
 */
export function evictOldCacheEntries() {
  evictOldEntriesIfNeeded()
}

/**
 * Clear the SVG cache (useful for memory management)
 */
export function clearSvgCache() {
  // Revoke all blob URLs and clear cache in one pass (faster than forEach)
  for (const key in svgCache) {
    const entry = svgCache[key]
    if (entry && entry.url && activeBlobUrls.has(entry.url)) {
      try {
        URL.revokeObjectURL(entry.url)
        activeBlobUrls.delete(entry.url)
      } catch (e) {
        // URL may already be revoked
      }
    }
    delete svgCache[key]
  }

  // Clear icon cache
  for (const key in iconCache) {
    delete iconCache[key]
  }
}

/**
 * Enable or disable SVG caching
 * @param {boolean} enabled - Whether to enable caching
 */
export function setCachingEnabled(enabled) {
  const wasEnabled = cachingEnabled
  cachingEnabled = enabled

  if (!enabled) {
    // Clear cache when disabling
    clearSvgCache()
  } else if (!wasEnabled && enabled) {
    // When re-enabling, clean up any pending non-cached blob URLs
    cleanupNonCachedBlobUrls()
  }
}

/**
 * Check if caching is currently enabled
 * @returns {boolean} Whether caching is enabled
 */
export function isCachingEnabled() {
  return cachingEnabled
}

/**
 * Get cache statistics for monitoring
 */
export function getCacheInfo() {
  let totalSize = 0
  let cacheSize = 0
  const cacheDetails = []
  const cacheKeys = []

  // Use for...in loop (faster than forEach + Object.keys)
  for (const key in svgCache) {
    cacheSize++
    cacheKeys.push(key)
    const entry = svgCache[key]
    if (entry) {
      totalSize += entry.size || 0
      // One label for UI: waypoint entries have icon/color/width; generic SVGs have componentName/key
      const displayLabel = entry.icon != null
        ? `${entry.icon} - ${entry.color || ''} (${entry.width != null ? entry.width : '?'}px)`
        : (entry.componentName || key)
      cacheDetails.push({
        key: key,
        accessCount: entry.accessCount || 0,
        color: entry.color,
        componentName: entry.componentName,
        createdAt: entry.createdAt,
        displayLabel: displayLabel,
        icon: entry.icon,
        lastAccessed: entry.lastAccessed,
        size: entry.size,
        width: entry.width,
      })
    }
  }

  // Count icon cache size
  let iconCacheSize = 0
  for (const key in iconCache) {
    iconCacheSize++
  }

  return {
    svgCacheSize: cacheSize,
    iconCacheSize: iconCacheSize,
    cacheKeys: cacheKeys,
    totalSizeBytes: totalSize,
    totalSizeMB: Math.round((totalSize / 1024 / 1024) * 100) / 100,
    cacheDetails: cacheDetails,
    activeBlobUrls: activeBlobUrls.size,
    nonCachedBlobUrls: nonCachedBlobUrls.size,
    cachingEnabled: cachingEnabled
  }
}

/**
 * Convert SVG markup to data URI (blob URL)
 * @param {string} svgMarkup - SVG markup string
 * @returns {string} Blob URL
 */
function exportSVG(svgMarkup) {
  try {
    if (!svgMarkup || typeof svgMarkup !== 'string') {
      throw new Error('Invalid SVG markup provided')
    }

    const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)

    // Track blob URL for proper cleanup
    activeBlobUrls.add(url)

    // Note: We do NOT revoke blob URLs here because they are cached and may be used later
    // They will be revoked when clearSvgCache() is called

    return url
  } catch (error) {
    console.error('Error creating blob URL from SVG:', error)
    // Return empty data URI as last resort
    return 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg"></svg>'
  }
}

/**
 * Preload commonly used SVG icons and components
 * This preloads the most frequently used SVGs (eyeball icons, group, pushpin, etc.)
 * to ensure they're ready immediately when the app loads and prevent thousands of re-renders
 */
function preloadCommonIcons() {
  try {
    const commonIcons = [{
      color: 'rgba(243,119,53,1)',
      name: 'gohunt-logo',
    }]

    for (let i = 0; i < commonIcons.length; i++) {
      const icon = commonIcons[i]
      try {
        getCachedWaypointDataURI(icon.name, icon.color)
      } catch (error) {
        // Log but continue with other icons
        console.warn('Failed to preload waypoint icon:', icon.name, error)
      }
    }
  } catch (error) {
    // Don't break app initialization if preloading fails
    console.error('Error during icon preloading:', error)
  }
}

/**
 * Preload common SVG components
 * This preloads the most frequently used SVGs (eyeball icons, group, pushpin, etc.)
 * to ensure they're ready immediately when the app loads and prevent thousands of re-renders
 */
function preloadCommonSvgs() {
  try {
    // Common SVG components to preload
    // Each object defines: component, componentName, and props
    const commonSvgs = [
      {
        component: EyeOnIcon,
        name: 'EyeOnIcon',
        props: EYE_ON_PRELOAD_PROPS
      },
      {
        component: EyeOffIcon,
        name: 'EyeOffIcon',
        props: EYE_OFF_PRELOAD_PROPS
      },
      {
        component: Group,
        name: 'Group',
        props: { color: '#888888', width: 24, height: 24 }
      },
      {
        component: Pushpin,
        name: 'Pushpin',
        props: { color: '#F37735', width: 12, height: 12 }
      },
      {
        component: LocationPinIcon,
        name: 'LocationPinIcon',
        props: {}
      }
    ]

    // Loop through and preload each common SVG
    for (let i = 0; i < commonSvgs.length; i++) {
      const svgConfig = commonSvgs[i]
      try {
        getCachedSvgDataURI(svgConfig.component, svgConfig.name, svgConfig.props)
      } catch (error) {
        console.warn(`Failed to preload ${svgConfig.name}:`, error)
      }
    }
  } catch (error) {
    // Don't break app initialization if preloading fails
    console.error('Error during icon preloading:', error)
  }
}

// ── Location-pin helpers ───────────────────────────────────────────────────────

/**
 * Return a PictureMarkerSymbol for the settled location pin.
 * The underlying blob URL is cached via getCachedSvgDataURI so it is only
 * rendered once and served from memory on every subsequent call.
 */
export function getLocationPinSymbol() {
  const url = getCachedSvgDataURI(LocationPinIcon, 'LocationPinIcon', {})
  return { type: 'picture-marker', url, width: '34px', height: '34px' }
}

// Cached raw SVG markup for the loading spinner DOM element.
// We render the component once and reuse the string — no per-call overhead.
let _locationPinLoadingMarkup = null

/**
 * Return the static SVG markup string for the loading spinner.
 * CSS `animation: ghLocSpin` on the parent div rotates this in both 2D and 3D.
 */
export function getLocationPinLoadingMarkup() {
  if (_locationPinLoadingMarkup !== null) return _locationPinLoadingMarkup
  _locationPinLoadingMarkup = ReactDOMServer.renderToStaticMarkup(
    React.createElement(LocationPinLoadingIcon, null)
  )
  return _locationPinLoadingMarkup
}

// ──────────────────────────────────────────────────────────────────────────────

/**
 * Set caching from config and optionally preload common SVGs.
 * Only runs preload when caching is explicitly enabled so no unnecessary work when disabled.
 * When caching is off, getCachedSvgDataURI/getCachedWaypointDataURI still return valid blob URLs so SVGs render; they just are not stored.
 *
 * @param {boolean} [cachingEnabled] - From app config (e.g. uapiConfig.SVG_CACHING_ENABLED). Must be true to enable cache and run preload.
 */
export function preloadSvgCache(cachingEnabled) {
  setCachingEnabled(cachingEnabled)
  if (!cachingEnabled) {
    return
  }
  preloadCommonIcons()
  preloadCommonSvgs()
}
